import {oneTrustCookieKeyToCategory} from "./constants.ts";
import {defaultCookieConsent} from "./default-cookie-consent.ts";
import type {CookieCategory} from "./types.ts";

/**
 * Parse the consent cookie string.
 *
 * Parses a consent cookie string set by OneTrust and returns a map of cookie
 * categories to boolean values indicating whether the user has consented to
 * each category.
 *
 * @param cookie - The consent cookie string to parse.
 * @returns A map of cookie categories to boolean values.
 */
export function parseConsentCookie(
    cookie: string,
): ReadonlyMap<CookieCategory, boolean> {
    // sync-start:parse-consent-cookie 1354321792 https://github.com/Khan/webapp/blob/master/pkg/web/cookie_consent.go
    // This logic is specific to parsing a cookie set by OneTrust:
    // https://developer.onetrust.com/onetrust/docs/onetrust-cookies#optanonconsent
    // The cookie value contains several ampersand-delimited "fields," where
    // we're interested in the "groups" field, which contains the information
    // about which cookie categories the user has consented for us to use.
    // This will have the form of 'C0001:1,C0003:1,C0002:1,C0004:1', where the
    // keys map to the cookie categories defined in the CookieCategory enum
    const rawCookieConsentGroups = new URLSearchParams(cookie).get("groups");

    // If the cookie is malformed, we'll just return the default cookie consent
    if (rawCookieConsentGroups == null) {
        return defaultCookieConsent();
    }

    // Now we need to parse the string form of the cookie consent groups into
    // a Map<CookieCategory, boolean>
    const parsedCookieConsentGroups: Map<CookieCategory, boolean> = new Map();
    for (const pair of rawCookieConsentGroups.split(",")) {
        // pair will have the form of 'C0001:1'
        const [rawCategory, value] = pair.split(":");
        // get the enum cookie category from the string category
        const category = oneTrustCookieKeyToCategory.get(rawCategory);
        if (category) {
            parsedCookieConsentGroups.set(category, value === "1");
        } else {
            // something is malformed, return default cookie consent
            return defaultCookieConsent();
        }
    }

    // If the cookie is missing any categories, we'll return the default cookie
    // consent
    return parsedCookieConsentGroups.size === oneTrustCookieKeyToCategory.size
        ? parsedCookieConsentGroups
        : defaultCookieConsent();
    // sync-end:parse-consent-cookie
}
